import { useState } from 'react';
import { Layer, Marker, Source } from 'react-map-gl/maplibre';
import { circleRing } from '../../utils/geo';

/**
 * The proposal a geolocated detection makes before it becomes a map point.
 *
 * Rendered as a child of <Map> — Source/Layer only work inside the map subtree — and kept in
 * its own component so App.tsx gains a few lines rather than a hundred.
 *
 * The accuracy circle is the reason this screen exists. A bare pin invites the operator to
 * trust a precision the system does not have; the circle is the honest claim, and it is
 * measured rather than modelled (backend/camera_calibration.py propagates the anchor pixel's
 * own uncertainty through the transform). Drawn dashed, like the assistant's proposals, so it
 * reads as "not saved yet" at a glance.
 */

export interface PendingFix {
  lat: number;
  lng: number;
  accuracy_m: number;
  label: string;
  warnings: string[];
}

interface GeolocatedPreviewProps {
  fix: PendingFix;
  onConfirm: (name: string) => void;
  onDismiss: () => void;
  busy?: boolean;
}

const WARNING_TEXT: Record<string, string> = {
  outside_calibration: 'Outside the area you calibrated — treat the position as approximate.',
  far_beyond_calibration: 'Well beyond the calibrated ground. Accuracy degrades quickly here.',
};

export default function GeolocatedPreview({
  fix, onConfirm, onDismiss, busy,
}: GeolocatedPreviewProps) {
  const [name, setName] = useState(fix.label);

  const ring = circleRing(fix.lat, fix.lng, Math.max(fix.accuracy_m, 1));
  const circle: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: [ring] },
    }],
  };

  return (
    <>
      <Source id="geolocated-accuracy" type="geojson" data={circle}>
        <Layer
          id="geolocated-accuracy-fill" type="fill"
          paint={{ 'fill-color': '#ffd166', 'fill-opacity': 0.12 }}
        />
        <Layer
          id="geolocated-accuracy-line" type="line"
          paint={{ 'line-color': '#ffd166', 'line-width': 1.5, 'line-dasharray': [3, 2] }}
        />
      </Source>

      <Marker longitude={fix.lng} latitude={fix.lat} anchor="bottom">
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'rgba(10,12,16,0.92)', border: '1px solid var(--color-warning)', borderRadius: 4,
            padding: '8px 10px', minWidth: 220, color: '#e8ecf1', fontSize: 12,
            boxShadow: '0 6px 18px rgba(0,0,0,0.5)', marginBottom: 10,
          }}
        >
          <div style={{
            color: 'var(--color-warning)', fontSize: 11, letterSpacing: '0.06em', marginBottom: 6,
          }}>
            PLACE HERE · ±{fix.accuracy_m.toFixed(1)} m
          </div>

          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onConfirm(name.trim()); }}
            maxLength={255}
            style={{
              width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: 3, color: '#fff',
              padding: '4px 6px', fontSize: 12, marginBottom: 6,
            }}
          />

          {fix.warnings.map(w => (
            <div key={w} style={{ color: '#ffb4b4', fontSize: 11, marginBottom: 6 }}>
              {WARNING_TEXT[w] || w}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => name.trim() && onConfirm(name.trim())}
              disabled={busy || !name.trim()}
              style={{
                flex: 1, background: 'var(--color-warning)', color: '#101318', border: 'none',
                borderRadius: 3, padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                letterSpacing: '0.04em', opacity: busy || !name.trim() ? 0.55 : 1,
              }}
            >{busy ? 'SAVING…' : 'CONFIRM'}</button>
            <button
              onClick={onDismiss}
              style={{
                background: 'transparent', color: '#c8ced6',
                border: '1px solid rgba(255,255,255,0.28)', borderRadius: 3,
                padding: '4px 8px', fontSize: 11, cursor: 'pointer', letterSpacing: '0.04em',
              }}
            >DISMISS</button>
          </div>
        </div>
      </Marker>
    </>
  );
}
